import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import * as dayjs from 'dayjs';
import { createSchedules, detailsSchedule } from '../../../services/practices';
import { teamDetails } from '../../../services/teams';
import LibraryDialog from '../../../components/shared/libraryDialog';
import DateDialog from './date-dialog';
import TimeInput from "react-time-picker-input";

const EditorSchedule = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { teamId } = useParams();

  const [team, setTeam] = useState({ minutes_per_practice: 75 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isDataDialog, setIsDataDialog] = useState(true);
  const [scheduleData, setScheduleData] = useState([]);
  const [selectedPracticeIndex, setSelectedPracticeIndex] = useState(null);
  const [scheduleStatus, setScheduleStatus] = useState('waiting');
  const [scheduleConfig, setScheduleConfig] = useState({
    dates: [],
    range: [null, null],
    isRegular: false,
    weekDays: [],
    time: [],
  });

  useEffect(() => {
    const getTeam = async () => {
      const { team } = await teamDetails(teamId);
      setTeam(team);
    };

    teamId && getTeam();
  }, [teamId]);

  const select = item => {
    changeSchedule('practice', selectedPracticeIndex, item)
    setSelectedPracticeIndex(null);
  };

  const changeSchedule = (field, index, value) => {
    const updatedSchedule = [...scheduleData];
    updatedSchedule[index][field] = value;
    setScheduleData(updatedSchedule);
  };

  const remove = index => {
    const updatedSchedule = scheduleData.filter((_, i) => i !== index);
    setScheduleData(updatedSchedule);
  };

  const save = async () => {
    const schedule = scheduleData.flatMap(entry => {
      const scheduledAt = dayjs(entry.date)
        .set('hour', entry.time.split(':')[0])
        .set('minute', entry.time.split(':')[1])
        .format('YYYY-MM-DDTHH:mm:ss');
      return {
        scheduled_at: scheduledAt,
        practice_template_id: entry.practice?.id,
        duration: entry.duration,
      };
    });
    const payload = {
      team_id: teamId,
      schedule: schedule,
    };
    try {
      const response = await createSchedules(payload);
      if (response && response.schedule.id) {
        const scheduleId = response.schedule.id;
        const intervalId = setInterval(() => checkStatus(scheduleId, teamId, intervalId), 500);
      } else {
        console.error('No schedule ID found in response.');
      }
      setDialogOpen(true);
    } catch (error) {
      console.error('Error creating practice:', error);
    }
  };

  const checkStatus = async (id, teamId, intervalId) => {
    try {
      const scheduleDetails = await detailsSchedule(id, teamId);
      if (scheduleDetails.schedule.status === 'done') {
        setScheduleStatus('done');
        clearInterval(intervalId);
      }
    } catch (error) {
      console.error('Error checking schedule status:', error);
      clearInterval(intervalId);
    }
  };

  const generateSchedule = (dates, isRegular) => {
    return isRegular
      ? dates.map(([date, time = '00:00']) => ({ date, time, practice: null }))
      : dates.map(date => ({ date, time: '00:00', practice: null }));
  };

  const handleSaveDates = ({ dates, range, isRegular, weekDays, time }) => {
    const newSchedule = generateSchedule(dates, isRegular);
    setScheduleData(newSchedule);
    setIsDataDialog(false);
    setScheduleConfig({ dates, range, isRegular, weekDays, time });
  };

  const hasScheduleChanged = () => {
    const initialEntries = generateSchedule(scheduleConfig.dates, scheduleConfig.isRegular);
    return scheduleData.some((entry, index) => {
      const initialEntry = initialEntries[index];
      return (
        !initialEntry ||
        entry.time !== initialEntry.time ||
        entry.practice !== initialEntry.practice ||
        (scheduleConfig.isRegular && entry.date !== initialEntry.date)
      );
    });
  };

  return (
    <div className="form-container practice-form">
      <div className="team-details-header">
        <div className="team-details-header-text">{t('schedule.create')}</div>
        <button
          className="button button-add"
          onClick={() => hasScheduleChanged() && !window.confirm(t('schedule.changes_will_be_lost')) ? null : setIsDataDialog(true)}
        >
          {t('schedule.change_dates')}
        </button>
      </div>
      <div className="group-date">
        <div className="group-date-header">
          <div className="group-day">{t('schedule.date')}</div>
          <div className="group-hour">{t('schedule.hour')}</div>
          <div className="group-duration">{t('schedule.minutes_per_practice')}</div>
          <div className="group-practice">{t('schedule.practice')}</div>
          <div className="group-actions"></div>
        </div>
        {scheduleData.map((entry, index) => (
          <div className="group-date-item" key={index}>
            <div className="group-day">{dayjs(entry.date).format('dddd, D MMM')}</div>
            <div className="group-hour">
              <TimeInput value={entry.time} onChange={e => changeSchedule('time', index, e)}/>
            </div>
            <div className="group-duration">
              <input type="number" value={entry?.duration || ''} min={5} max={150} placeholder={team?.minutes_per_practice} onChange={e => changeSchedule('duration', index, e.target.value)} />
            </div>
            <div className="group-practice">
              {entry.practice ? (
                <>
                  <strong>{entry.practice.title}</strong>
                  <div className="group-practice-buttons">
                    <i
                      className="icon icon-pencil pointer"
                      onClick={() => setSelectedPracticeIndex(index)}
                    ></i>

                    <div onClick={() => changeSchedule('practice', index, null)}>
                      <span>&times;</span>
                    </div>
                  </div>
                </>
              ) : (
                <div
                  className="supercoach-container"
                  onClick={() => setSelectedPracticeIndex(index)}
                >
                  Supercoach <i aria-hidden="true" className="icon icon-arrow-right pointer"></i>
                </div>
              )}
            </div>
            <div className="group-actions">
              <i className="icon icon-trash pointer" onClick={() => remove(index)}></i>
            </div>
          </div>
        ))}
      </div>
      <div className="form-container-actions">
        <div className="flex-end">
          <button className="button" onClick={() => navigate(`/club_admin/teams/${teamId}`)}>
            {t('buttons.common.cancel')}
          </button>
          <button className="button button-primary" onClick={() => save()}>
            {t('schedule.create')}
          </button>
        </div>
      </div>
      {selectedPracticeIndex !== null && (
        <LibraryDialog
          close={() => setSelectedPracticeIndex(null)}
          onChange={(_, item) => select(item)}
          libraries={['PracticeTemplates']}
        />
      )}
      {isDataDialog && (
        <DateDialog
          close={() => setIsDataDialog(false)}
          onSaveDates={handleSaveDates}
          initialDates={{
            dates: scheduleConfig.isRegular ? scheduleConfig.dates : scheduleData.map(entry => entry.date),
            range: scheduleConfig.range,
            isRegular: scheduleConfig.isRegular,
            weekDays: scheduleConfig.weekDays,
            time: scheduleConfig.time,
          }}
        />
      )}
      {dialogOpen && (
        <GeneratingScheduleDialog close={() => setDialogOpen(false)} teamId={teamId} scheduleStatus={scheduleStatus} />
      )}
    </div>
  );
};

export default EditorSchedule;

const GeneratingScheduleDialog = ({ teamId, scheduleStatus, close }) => {
  const [showButtons, setShowButtons] = useState(false);
  const [showText, setShowText] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ref = useRef();

  useEffect(() => {
    ref.current?.showModal();
    if (scheduleStatus === 'done') return setShowButtons(true);
    const buttonsTimeout = setTimeout(() => setShowButtons(true), 2000);
    const textTimeout = setTimeout(() => setShowText(true), 5000);

    return () => {
      clearTimeout(buttonsTimeout);
      clearTimeout(textTimeout);
    };
  }, [scheduleStatus]);

  const closeDialog = () => navigate(`/club_admin/teams/${teamId}`);

  return (
    <dialog ref={ref} onCancel={close} className="notification-dialog fade-in-top">
      {showButtons && (
        <div className="close light" onClick={closeDialog}></div>
      )}
      <div className="content-dialog">
        <div className="content-dialog-logo">
          <img src="/images/logo.png" alt="logo" />
        </div>
        {scheduleStatus === 'done' ? (
          <div className="content-dialog-title scale-up-center">
            <div>{t('dialog.ready_schedule')}</div>
          </div>
        ) : (
          <div className="content-dialog-title">
            <div>{t('dialog.generating_schedule')}</div>
            <div className="loader"></div>
          </div>
        )}
        <div className="content-dialog-name">
          <p>{showText ? t('dialog.generating_schedule_description1') : t('dialog.generating_schedule_description')}</p>
        </div>
        {showButtons && (
          <div className="button-outline-white" onClick={closeDialog}>
            {t('dialog.ok')}
          </div>
        )}
      </div>
    </dialog>
  );
};
